/**
 * Authentication store (zustand).
 *
 * Holds the current user (if any) and exposes login / logout / refresh.
 * The actual session state lives in an HttpOnly cookie set by the Worker —
 * this store is purely a client-side cache of GET /api/auth/me.
 */
import { create } from 'zustand';
import { api, ApiError } from '../lib/api';

export type UserRole = 'admin' | 'student';

export interface AuthUser {
  id: string;
  name: string;
  role: UserRole;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  login: (name: string, pin: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  loading: false,
  initialized: false,
  error: null,

  refresh: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.get<{ user: AuthUser }>('/api/auth/me');
      set({ user: data.user, loading: false, initialized: true });
    } catch (e) {
      // 401 = not logged in, that's fine
      if (e instanceof ApiError && e.status === 401) {
        set({ user: null, loading: false, initialized: true });
        return;
      }
      set({
        user: null,
        loading: false,
        initialized: true,
        error: (e as Error).message,
      });
    }
  },

  login: async (name: string, pin: string) => {
    set({ loading: true, error: null });
    try {
      const data = await api.post<{ user: AuthUser }>('/api/auth/login', {
        name,
        pin,
      });
      set({ user: data.user, loading: false, initialized: true });
      return data.user;
    } catch (e) {
      const message = e instanceof ApiError ? e.message : (e as Error).message;
      set({ loading: false, error: message });
      throw e;
    }
  },

  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // ignore — we still want to clear local state
    }
    set({ user: null, error: null });
  },
}));
